import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TrueCallingLogo } from '../components/TrueCallingLogo';
import { useAuth } from '../context/AuthContext';
import { testSupabaseConnection, EDGE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from '../services/supabaseClient';
import { consumeKickedFlag } from '../services/sessionGuard';
import { t } from '../utils/translations';
import { isProEmail } from '../utils/proEmail';

/**
 * Password reset requires Supabase configuration:
 * 1. Auth → Authentication → Providers → Email → Enable "Email Authentication"
 * 2. Auth → URL Configuration → Redirect URLs → Add all these:
 *    - http://localhost:3000/reset-password
 *    - http://localhost:3001/reset-password
 *    - https://truecallingv2.ai/reset-password
 *    - https://your-domain.com/reset-password
 * 3. Email provider must be configured (default or custom SMTP)
 */

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Cible post-login : honore ?redirect= (ex. retour vers /oauth/consent) mais
  // UNIQUEMENT un chemin interne (garde anti open-redirect : doit commencer par
  // un seul "/" — bloque //evil.com et /\evil). Sinon défaut habituel.
  const redirectParam = searchParams.get('redirect');
  const postLoginTarget =
    redirectParam && /^\/(?![/\\])/.test(redirectParam) ? redirectParam : '/menupb2b';
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  // Signup panel state. La création de compte passe désormais par la page publique /signup ; ce
  // panneau reste joignable par /login?signup=join (rejoindre l'espace existant de son entreprise).
  const [mode, setMode] = useState<'login' | 'signup'>(() => (searchParams.get('signup') === 'join' ? 'signup' : 'login'));
  const [suFirst, setSuFirst] = useState('');
  const [suLast, setSuLast] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suEntQuery, setSuEntQuery] = useState('');
  const [suEntId, setSuEntId] = useState<string | null>(null);
  const [suEntName, setSuEntName] = useState('');
  const [suNewEnt, setSuNewEnt] = useState(false);
  const [suSuggestions, setSuSuggestions] = useState<Array<{ id: string; name: string }>>([]);
  const [suSent, setSuSent] = useState(false);
  // Issue renvoyée par auth-signup-request : 'created' (compte créé tout de suite),
  // 'existing' (l'email a déjà un compte → lien d'accès renvoyé) ou 'pending'
  // (demande de rejoindre une entreprise existante → un responsable tranche).
  const [suMode, setSuMode] = useState<'created' | 'existing' | 'pending'>('pending');
  // Single-device eviction banner (shown once after being kicked from another device)
  const [kicked, setKicked] = useState(false);
  const [language] = useState<string>(() => {
    try { const s = localStorage.getItem('tc-language'); if (s) return JSON.parse(s); } catch {}
    return 'fr';
  });

  useEffect(() => {
    if (user.loggedIn) navigate(postLoginTarget);
  }, [user.loggedIn, navigate, postLoginTarget]);

  useEffect(() => { setKicked(consumeKickedFlag()); }, []);

  // Debounced enterprise type-ahead for the signup panel.
  useEffect(() => {
    if (suNewEnt || suEntQuery.trim().length < 2 || !EDGE_FUNCTIONS_URL) { setSuSuggestions([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetch(`${EDGE_FUNCTIONS_URL}/enterprises-search`, {
        method: 'POST', signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ q: suEntQuery.trim() }),
      }).then(r => r.json()).then(j => setSuSuggestions(Array.isArray(j.results) ? j.results : [])).catch(() => {});
    }, 250);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [suEntQuery, suNewEnt]);

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card" style={{ textAlign: 'center', color: 'var(--gray-400)' }}>
          {t(language, 'loginLoading')}
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const cleanEmail = email.trim().replace(/^mailto:/i, '');
    if (!cleanEmail || !password) { setError(t(language, 'loginErrEmailPasswordRequired')); return; }
    setError('');
    setIsLoading(true);

    const t0 = performance.now();
    try {
      await login(cleanEmail, password);
      const authTime = performance.now() - t0;
      console.log(`[Login] Auth completed in ${authTime.toFixed(0)}ms`);
      // Auth succeeded → redirect immediately instead of waiting for useEffect
      navigate(postLoginTarget);
    } catch (err: any) {
      const errTime = performance.now() - t0;
      const errorMsg = err?.message || t(language, 'loginErrLoginFailed');
      console.error(`[Login] Failed after ${errTime.toFixed(0)}ms:`, errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError(t(language, 'loginErrEnterEmailFirst')); return; }
    setError('');
    setIsLoading(true);
    try {
      // UI language (pre-auth) for the email — same source as DashboardContext.
      let lang = 'fr';
      try { const s = localStorage.getItem('tc-language'); if (s) lang = JSON.parse(s); } catch {}
      if (!EDGE_FUNCTIONS_URL) throw new Error(t(language, 'loginErrResetSendFailed'));
      // Sends via the SendGrid-backed edge fn (Supabase default SMTP is rate-limited). The
      // response is always ok (anti-enumeration), so we show the same confirmation whether
      // or not the account exists.
      await fetch(`${EDGE_FUNCTIONS_URL}/auth-send-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ email: email.trim().toLowerCase(), language: lang }),
      });
      setResetSent(true);
    } catch (err: any) {
      setError(err?.message || t(language, 'loginErrResetSendFailedCatch'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    setError('');
    if (!suFirst.trim() || !suEmail.trim()) { setError(t(language, 'loginErrFirstNameEmailRequired')); return; }
    if (!isProEmail(suEmail)) { setError(t(language, 'loginErrProEmailRequired')); return; }
    if (!suNewEnt && !suEntId) { setError(t(language, 'loginErrSelectEnterprise')); return; }
    if (suNewEnt && !suEntName.trim()) { setError(t(language, 'loginErrEnterEnterpriseName')); return; }
    // Pb 1 — éviter un doublon : si une entreprise du même nom existe déjà, demander de la sélectionner.
    if (suNewEnt && EDGE_FUNCTIONS_URL) {
      try {
        const r = await fetch(`${EDGE_FUNCTIONS_URL}/enterprises-search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
          body: JSON.stringify({ q: suEntName.trim() }),
        });
        const j = await r.json();
        const exists = Array.isArray(j.results)
          && j.results.some((e: { name?: string }) => (e.name ?? '').trim().toLowerCase() === suEntName.trim().toLowerCase());
        if (exists) { setError(t(language, 'loginErrEnterpriseAlreadyExists')); return; }
      } catch { /* réseau indisponible : le dédoublonnage à l\'approbation reste le filet de sécurité */ }
    }
    setIsLoading(true);
    try {
      let lang = 'fr';
      try { const s = localStorage.getItem('tc-language'); if (s) lang = JSON.parse(s); } catch {}
      if (!EDGE_FUNCTIONS_URL) throw new Error(t(language, 'loginErrSignupSubmitFailed'));
      const suRes = await fetch(`${EDGE_FUNCTIONS_URL}/auth-signup-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          firstName: suFirst.trim(), lastName: suLast.trim(), email: suEmail.trim().toLowerCase(),
          enterpriseId: suNewEnt ? undefined : suEntId,
          enterpriseName: suNewEnt ? suEntName.trim() : suEntName,
          newEnterprise: suNewEnt, language: lang,
        }),
      });
      try {
        const j = await suRes.json();
        // Domaine sans MX / MX nul / inexistant : l'e-mail d'accès n'arriverait jamais.
        if (j?.error === 'email-undeliverable') {
          setError(t(language, 'signupErrUndeliverable').split('{domain}').join(String(j.domain ?? '')));
          return;
        }
        if (j?.mode === 'created' || j?.mode === 'existing') setSuMode(j.mode);
        else setSuMode('pending');
      } catch { setSuMode('pending'); }
      setSuSent(true);
    } catch (err: any) {
      setError(err?.message || t(language, 'loginErrSignupSubmitFailedCatch'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <TrueCallingLogo size={34} inkColor="var(--gray-900)" showTagline />
        </div>

        {kicked && (
          <div role="alert" style={{
            background: 'var(--c-orange-bg)', border: '1px solid var(--c-orange-border)', color: 'var(--c-orange-text-5)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13,
          }}>
            {t(language, 'sessionKickedBanner')}
          </div>
        )}

        {mode === 'signup' ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <h1 className="login-title">{t(language, 'loginSignupTitle')}</h1>
            {suSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{suMode === 'pending' ? '📨' : '✅'}</div>
                <p style={{ color: 'var(--success)', fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
                  {suMode === 'pending' ? t(language, 'loginSignupSentTitle') : 'Votre espace est prêt'}
                </p>
                <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
                  {suMode === 'created'
                    ? "Votre compte a été créé. Consultez votre boîte mail : le lien reçu vous permet de définir votre mot de passe et d'entrer dans l'application."
                    : suMode === 'existing'
                      ? "Cette adresse a déjà un espace TrueCalling. Nous venons de vous envoyer un lien d'accès."
                      : t(language, 'loginSignupSentBody')}
                </p>
                <button className="login-btn" style={{ width: '100%' }} onClick={() => { setMode('login'); setSuSent(false); setError(''); }}>
                  {t(language, 'loginBackToLogin')}
                </button>
              </div>
            ) : (
              <>
                <input className="login-input" placeholder={t(language, 'loginPhFirstName')} value={suFirst} onChange={e => setSuFirst(e.target.value)} autoComplete="given-name" />
                <input className="login-input" placeholder={t(language, 'loginPhLastName')} value={suLast} onChange={e => setSuLast(e.target.value)} autoComplete="family-name" />
                <input className="login-input" type="email" placeholder={t(language, 'loginPhEmailSignup')} value={suEmail} onChange={e => setSuEmail(e.target.value.replace(/^mailto:/i, ''))} autoComplete="email" />
                {!suNewEnt ? (
                  <div style={{ position: 'relative' }}>
                    <input className="login-input" placeholder={t(language, 'loginPhEnterprise')} value={suEntQuery}
                      onChange={e => { setSuEntQuery(e.target.value); setSuEntId(null); }} autoComplete="off" />
                    {suSuggestions.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, zIndex: 10, maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                        {suSuggestions.map(s => (
                          <div key={s.id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                            onMouseDown={() => { setSuEntId(s.id); setSuEntName(s.name); setSuEntQuery(s.name); setSuSuggestions([]); }}>
                            {s.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input className="login-input" placeholder={t(language, 'loginPhEnterpriseName')} value={suEntName} onChange={e => setSuEntName(e.target.value)} />
                )}
                <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={suNewEnt} onChange={e => { setSuNewEnt(e.target.checked); setSuEntId(null); setSuEntName(''); setSuEntQuery(''); }} />
                  {t(language, 'loginNewEnterpriseCheckbox')}
                </label>
                <button type="button" className="login-btn" disabled={isLoading} onClick={handleSignup}>
                  {isLoading ? '…' : t(language, 'loginSubmitRequest')}
                </button>
                {error && <p className="login-error">{error}</p>}
                <button type="button" onClick={() => { setMode('login'); setError(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pink)', fontSize: 13, display: 'block', margin: '4px auto 0', textDecoration: 'underline' }}>
                  {t(language, 'loginBackToLoginArrow')}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
        <h1 className="login-title">{t(language, 'loginTitle')}</h1>

        {resetSent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
            <p style={{ color: 'var(--success)', fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
              {t(language, 'loginResetSentTitle')}
            </p>
            <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
              {t(language, 'loginResetSentBody')}
              <br />{t(language, 'loginResetLinkExpiry')}
            </p>
            <button
              onClick={() => { setResetSent(false); setEmail(''); setPassword(''); }}
              className="login-btn"
              style={{ width: '100%', marginBottom: 8 }}
            >
              {t(language, 'loginBackToLoginEn')}
            </button>
            <button
              onClick={() => { setResetSent(false); }}
              style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--pink)', fontSize: 13, textDecoration: 'underline'
              }}
            >
              {t(language, 'loginTryAnotherEmail')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleLogin}>
            <label className="login-label" htmlFor="email">{t(language, 'loginEmailLabel')}</label>
            <input
              id="email"
              type="email"
              className="login-input"
              value={email}
              onChange={e => setEmail(e.target.value.replace(/^mailto:/i, ''))}
              placeholder={t(language, 'loginPhEmail')}
              required
              autoComplete="email"
            />

            <label className="login-label" htmlFor="password" style={{ marginTop: 12 }}>{t(language, 'loginPasswordLabel')}</label>
            <input
              id="password"
              type="password"
              className="login-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />

            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? t(language, 'loginSigningIn') : t(language, 'loginSignIn')}
            </button>

            {error && <p className="login-error">{error}</p>}

            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isLoading}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--pink)', fontSize: 13, display: 'block',
                margin: '12px auto 0', textDecoration: 'underline',
              }}
            >
              {t(language, 'loginForgotPassword')}
            </button>

            <Link
              to="/signup"
              style={{
                color: 'var(--pink)', fontSize: 13, display: 'block', textAlign: 'center',
                margin: '8px auto 0', textDecoration: 'underline',
              }}
            >
              {t(language, 'loginNoAccountSignup')}
            </Link>
          </form>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
